import { Input } from "@ant-design/react-native";
import { Head } from "@shared/components/head";
import { Text } from "@shared/components/text";
import { useState } from "react";
import { dynamicTranslations } from "@shared/utils/dynamicTranslation";
import { TouchableOpacity, View, Keyboard } from "react-native";
import Button from "@shared/components/md-design-system/Button/Button";
import { CustomCheckbox } from "@shared/components/md-design-system/Checkbox/CustomCheckbox";
import { colorPalette } from "@shared/config/colorsConfig";
import { TDrugIntakeReason } from "app/_modules/core/types";

const { TextArea } = Input;

export interface IDrugIntakeSubmitForm {
    reasons: TDrugIntakeReason[];
    note?: string;
}

const REASONS: { value: TDrugIntakeReason; labelKey: string }[] = [
    { value: "ran_out", labelKey: "drugs.notTaken.answers.runOutOfMeds" },
    { value: "side_effects", labelKey: "drugs.notTaken.answers.gotSideEffects" },
    { value: "doctor_instructed", labelKey: "drugs.notTaken.answers.doctorInstructed" },
    { value: "other", labelKey: "drugs.notTaken.answers.otherReason" },
];

export const DrugIntakeSubmitForm = ({handleSubmit}: {handleSubmit: (data: IDrugIntakeSubmitForm) => void}) => {
    const { t } = dynamicTranslations();
    const [selectedReasons, setSelectedReasons] = useState<TDrugIntakeReason[]>([]);
    const [note, setNote] = useState<string>("");

    const toggleReason = (value: TDrugIntakeReason) => {
      setSelectedReasons((current) => {
        if (current.includes(value)) {
          if (value === "other") setNote("");
          return current.filter((reason) => reason !== value);
        }
        return [...current, value];
      });
    };

    const isOtherSelected = selectedReasons.includes("other");

    const isButtonDisabled = () => {
      if (selectedReasons.length === 0) return true;
      if (isOtherSelected && note.trim() === "") return true;
      return false;
    };

    const handleScreenPress = () => {
      Keyboard.dismiss();
    };

    return (
      <TouchableOpacity
        activeOpacity={1}
        onPress={handleScreenPress}
        className="flex-1 flex-col justify-between"
      >
        <View className="flex-col gap-6">
          <Head type="h2">{t("drugs.notTaken.question")}</Head>
          <View className="flex-col gap-4 bg-white">
            <Text>{t("drugs.notTaken.selectOneOrMoreOptions")}</Text>

            <View className='flex-col gap-2'>
              {REASONS.map(({ value, labelKey }) => {
                const isSelected = selectedReasons.includes(value);
                return (
                  <TouchableOpacity
                    key={value}
                    className={`flex-row items-center justify-between gap-2 p-4 border rounded-lg ${isSelected ? 'bg-primary-100 border-primary-700' : 'border-gray-300'}`}
                    onPress={() => toggleReason(value)}
                  >
                    <Text>{t(labelKey)}</Text>
                    <CustomCheckbox
                      checked={isSelected}
                      onChange={() => toggleReason(value)}
                      checkedColor={colorPalette.primary[700]}
                    />
                  </TouchableOpacity>
                );
              })}
            </View>

            {isOtherSelected && (
              <View className="border border-gray-300 rounded-lg p-3">
                <TextArea
                  rows={10}
                  value={note}
                  onChangeText={setNote}
                  style={{ height: 150 }}
                  placeholder={t("drugs.notTaken.notePlaceholder")}
                />
              </View>
            )}
          </View>
        </View>

        <View className="mt-6">
          <Button onPress={() => handleSubmit({ reasons: selectedReasons, ...(note.trim() && { note: note.trim() }) })} disabled={isButtonDisabled()}>
            {t("buttons.save")}
          </Button>
        </View>
      </TouchableOpacity>
    );
  };